import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import SelectBox from 'devextreme-react/select-box';
import TextArea from 'devextreme-react/text-area';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { RecordEditDiscussionPopup, opsGridFormPopupSizeKey } from '../opsDataGrid';
import { PatientFormPopup } from './PatientFormPopup';
import { TrackerEntryDetailView } from './TrackerEntryDetailView';
import { type PatientTrackingApi, patientTrackingApi } from './patientTrackingApi';
import {
  PATIENT_STATUS_OPTIONS,
  type CoveredEntityDto,
  type OurPharmacyDto,
  type PatientDto,
  type PatientStatus,
  type PatientTrackerFieldPolicy,
  type PatientUpsertBody,
  type TrackerEntryDto,
  type TrackerEntryUpsertBody
} from './patientTrackingTypes';
import {
  INLINE_NEW_PATIENT_ID,
  emptyTrackerEntryForm,
  trackerEntryToBody
} from './trackerEntryEditorUtils';
import type { TrackerBoardLiveData } from './useLiveRows';

export interface PatientTrackerEntryEditorProps {
  coveredEntities: CoveredEntityDto[];
  pharmacies: OurPharmacyDto[];
  patients: PatientDto[];
  onPatientsChanged: () => void;
  currentUserId: string;
  currentUserName?: string;
  currentUserAliases?: Iterable<string>;
  stateStorageKey?: string;
  api?: PatientTrackingApi;
  patientTrackingApiBase?: string;
  allowInlinePatientCreate?: boolean;
  fieldPolicy?: PatientTrackerFieldPolicy;
  live: TrackerBoardLiveData;
}

export interface PatientTrackerEntryEditorHandle {
  openView: (row: TrackerEntryDto) => void;
  openCreate: () => void;
}

export const PatientTrackerEntryEditor = forwardRef<
  PatientTrackerEntryEditorHandle,
  PatientTrackerEntryEditorProps
>(function PatientTrackerEntryEditor(
  {
    coveredEntities,
    pharmacies,
    patients,
    onPatientsChanged,
    currentUserId,
    currentUserName = 'User',
    currentUserAliases,
    stateStorageKey = 'patient-tracker-grid',
    api = patientTrackingApi,
    patientTrackingApiBase,
    allowInlinePatientCreate = true,
    fieldPolicy,
    live
  },
  ref
) {
  const allowCreate = fieldPolicy?.allowCreate !== false;
  const allowDelete = fieldPolicy?.allowDelete !== false;
  const readOnlyPatient = fieldPolicy?.readOnlyPatient === true;
  const { rows, reload, applyUpsert, rowEditors, beginEditRow, endEditRow } = live;

  const [editorOpen, setEditorOpen] = useState(false);
  const [formViewMode, setFormViewMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TrackerEntryUpsertBody>(emptyTrackerEntryForm);
  const [enrollmentChecked, setEnrollmentChecked] = useState(false);
  const [enrollmentDirty, setEnrollmentDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [patientPopupOpen, setPatientPopupOpen] = useState(false);
  const [patientPopupInitial, setPatientPopupInitial] = useState<Partial<PatientUpsertBody> | null>(null);

  const formCeOptions = useMemo(
    () =>
      coveredEntities
        .filter((c) => !form.pharmacyId || c.pharmacyId === form.pharmacyId)
        .map((c) => ({ id: c.id, name: c.coveredEntityName })),
    [coveredEntities, form.pharmacyId]
  );

  const pharmacyOptions = useMemo(
    () => pharmacies.map((p) => ({ id: p.id, name: p.pharmacyName })),
    [pharmacies]
  );

  const patientOptions = useMemo(() => {
    const opts: Array<{ id: string; label: string; patient: PatientDto | null }> = patients
      .filter((p) => !form.pharmacyId || p.pharmacyId === form.pharmacyId)
      .map((p) => ({
        id: p.id,
        label: p.dob ? `${p.fullName} — ${p.dob}` : p.fullName,
        patient: p
      }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    if (allowInlinePatientCreate) {
      opts.push({ id: INLINE_NEW_PATIENT_ID, label: '+ New patient…', patient: null });
    }
    return opts;
  }, [allowInlinePatientCreate, patients, form.pharmacyId]);

  const patientById = useMemo(() => new Map(patients.map((p) => [p.id, p])), [patients]);

  const resetFormForCreate = () => {
    setEditingId(null);
    setForm(emptyTrackerEntryForm());
    setEnrollmentChecked(false);
    setEnrollmentDirty(false);
    setFormError(null);
  };

  const openCreate = useCallback(() => {
    if (!allowCreate) return;
    resetFormForCreate();
    setFormViewMode(false);
    setEditorOpen(true);
  }, [allowCreate]);

  const openView = useCallback((row: TrackerEntryDto) => {
    setEditingId(row.id);
    setFormViewMode(true);
    setForm(trackerEntryToBody(row));
    setEnrollmentChecked(row.enrollmentForm);
    setEnrollmentDirty(false);
    setFormError(null);
    setEditorOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ openView, openCreate }), [openCreate, openView]);

  const enterEditMode = () => {
    if (!editingId) return;
    const lock = rowEditors.get(editingId);
    if (lock && lock.userId.toLowerCase() !== currentUserId.toLowerCase()) {
      window.alert(`${lock.displayName} is currently editing this issue.`);
      return;
    }
    setFormViewMode(false);
    beginEditRow(editingId);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    if (editingId && !formViewMode) endEditRow();
    setFormViewMode(false);
  };

  const openInlineNewPatient = () => {
    setPatientPopupInitial(form.pharmacyId ? { pharmacyId: form.pharmacyId } : null);
    setPatientPopupOpen(true);
  };

  const onPatientSaved = (patient: PatientDto) => {
    onPatientsChanged();
    setForm((f) => ({
      ...f,
      pharmacyId: patient.pharmacyId,
      patientId: patient.id,
      coveredEntityId: f.coveredEntityId ?? patient.homeCoveredEntityId
    }));
    setEnrollmentChecked(patient.enrollmentForm);
    setEnrollmentDirty(false);
  };

  const onPatientLookupChange = (value: string | null) => {
    if (value === INLINE_NEW_PATIENT_ID) {
      openInlineNewPatient();
      return;
    }
    const patient = value ? patientById.get(value) ?? null : null;
    setForm((f) => ({
      ...f,
      pharmacyId: patient?.pharmacyId ?? f.pharmacyId,
      patientId: value ?? '',
      coveredEntityId: f.coveredEntityId ?? patient?.homeCoveredEntityId ?? null
    }));
    if (patient) {
      setEnrollmentChecked(patient.enrollmentForm);
      setEnrollmentDirty(false);
    }
  };

  const save = async (addAnother = false) => {
    setSaving(true);
    setFormError(null);
    try {
      const body: TrackerEntryUpsertBody = {
        ...form,
        currentIssue: form.currentIssue.trim(),
        changeLog: form.changeLog?.trim() || null,
        internalNote: form.internalNote?.trim() || null,
        enrollmentForm: enrollmentDirty ? enrollmentChecked : null
      };
      if (!fieldPolicy?.hidePharmacy && !body.pharmacyId) throw new Error('Select a pharmacy.');
      if (!body.patientId) throw new Error('Pick a patient.');
      if (!body.coveredEntityId) {
        const patient = patientById.get(body.patientId);
        if (!patient?.homeCoveredEntityId) {
          throw new Error('Select a covered entity (the patient has no home CE).');
        }
        body.coveredEntityId = patient.homeCoveredEntityId;
      }
      const dto =
        editingId == null
          ? await api.createTrackerEntry(body)
          : await api.updateTrackerEntry(editingId, body);
      applyUpsert(dto);
      if (editingId) endEditRow();
      if (body.enrollmentForm !== null) onPatientsChanged();
      if (addAnother) {
        const keepPatientId = form.patientId;
        const keepCe = form.coveredEntityId;
        const keepPharmacyId = form.pharmacyId;
        resetFormForCreate();
        setForm((f) => ({
          ...f,
          status: form.status,
          pharmacyId: keepPharmacyId,
          patientId: keepPatientId,
          coveredEntityId: keepCe
        }));
        const patient = patientById.get(keepPatientId);
        if (patient) setEnrollmentChecked(patient.enrollmentForm);
      } else {
        setEditorOpen(false);
        setFormViewMode(false);
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    if (!window.confirm('Delete this tracker entry?')) return;
    setSaving(true);
    try {
      await api.deleteTrackerEntry(editingId);
      endEditRow();
      setEditorOpen(false);
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const selectedPatient = form.patientId ? patientById.get(form.patientId) ?? null : null;
  const viewEntry = useMemo(
    () => (editingId ? rows.find((r) => r.id === editingId) ?? null : null),
    [editingId, rows]
  );

  return (
    <>
      <RecordEditDiscussionPopup
        visible={editorOpen}
        viewMode={formViewMode}
        onRequestEdit={enterEditMode}
        title={
          editingId == null
            ? 'Add tracker entry'
            : formViewMode
              ? 'View tracker entry'
              : 'Edit tracker entry'
        }
        onClose={closeEditor}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={formError}
        discussionTargetType="tracker"
        discussionRecordId={editingId}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserAliases={currentUserAliases}
        discussionRecordLabel={
          editingId && selectedPatient
            ? `Tracker: ${selectedPatient.lastName}, ${selectedPatient.firstName}`
            : 'Tracker issue'
        }
        patientTrackingApiBase={patientTrackingApiBase}
        footer={
          <div className="patient-tracking-popup-footer">
            <div>
              {editingId != null && allowDelete ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div className="patient-tracking-popup-footer__actions">
              <Button text="Cancel" disabled={saving} onClick={closeEditor} />
              {editingId == null && allowCreate ? (
                <Button text="Save & add another" disabled={saving} onClick={() => void save(true)} />
              ) : null}
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving}
                onClick={() => void save(false)}
              />
            </div>
          </div>
        }
      >
        {formViewMode ? (
          viewEntry ? (
            <TrackerEntryDetailView row={viewEntry} fieldPolicy={fieldPolicy} />
          ) : (
            <div className="patient-tracking-muted-text">Loading…</div>
          )
        ) : (
          <div className="patient-form-grid">
            {!fieldPolicy?.hidePharmacy ? (
              <label>
                <span className="patient-form-label patient-form-label--required">Pharmacy</span>
                <SelectBox
                  dataSource={pharmacyOptions}
                  valueExpr="id"
                  displayExpr="name"
                  searchEnabled
                  value={form.pharmacyId || null}
                  placeholder="Select pharmacy…"
                  onValueChanged={(e) =>
                    setForm((f) => ({
                      ...f,
                      pharmacyId: (e.value as string) ?? '',
                      patientId: '',
                      coveredEntityId: null
                    }))
                  }
                />
              </label>
            ) : null}
            <label>
              <span className="patient-form-label patient-form-label--required">Status</span>
              <SelectBox
                dataSource={PATIENT_STATUS_OPTIONS}
                valueExpr="value"
                displayExpr="label"
                value={form.status}
                onValueChanged={(e) =>
                  setForm((f) => ({ ...f, status: (e.value as PatientStatus) ?? 'Pending' }))
                }
              />
            </label>
            {readOnlyPatient && editingId ? (
              <label>
                <span className="patient-form-label">Patient</span>
                <div className="patient-tracking-muted-text" style={{ padding: '8px 0' }}>
                  {viewEntry?.patientDob
                    ? `${viewEntry.patientName} · DOB ${viewEntry.patientDob}`
                    : viewEntry?.patientName ?? selectedPatient?.lastName
                      ? `${selectedPatient?.lastName}, ${selectedPatient?.firstName}`
                      : '—'}
                </div>
              </label>
            ) : (
              <label>
                <span className="patient-form-label patient-form-label--required">Patient</span>
                <SelectBox
                  dataSource={patientOptions}
                  valueExpr="id"
                  displayExpr="label"
                  searchEnabled
                  value={form.patientId || null}
                  placeholder="Search patient or add new…"
                  onValueChanged={(e) => onPatientLookupChange((e.value as string) ?? null)}
                />
              </label>
            )}
            {!fieldPolicy?.hideCoveredEntity ? (
              <label>
                <span className="patient-form-label patient-form-label--required">CE to Alert</span>
                <SelectBox
                  dataSource={formCeOptions}
                  valueExpr="id"
                  displayExpr="name"
                  searchEnabled
                  value={form.coveredEntityId}
                  placeholder={
                    selectedPatient?.homeCoveredEntityName
                      ? `Defaults to home CE: ${selectedPatient.homeCoveredEntityName}`
                      : 'Select covered entity…'
                  }
                  onValueChanged={(e) =>
                    setForm((f) => ({ ...f, coveredEntityId: (e.value as string) ?? null }))
                  }
                />
              </label>
            ) : null}
            <label>
              <CheckBox
                text="Enrollment form on file (patient-wide)"
                value={enrollmentChecked}
                onValueChanged={(e) => {
                  setEnrollmentChecked(!!e.value);
                  setEnrollmentDirty(true);
                }}
              />
              {selectedPatient && enrollmentDirty ? (
                <div className="patient-tracking-muted-text" style={{ fontSize: 11, marginTop: 4 }}>
                  Saving will update the patient record (single source of truth).
                </div>
              ) : null}
            </label>

            <div className="patient-form-divider" />
            <div className="patient-form-group-label">Issue details</div>
            <label className="patient-form-grid__full">
              <span className="patient-form-label patient-form-label--required">Current issue</span>
              <TextArea
                value={form.currentIssue}
                className="patient-form-textarea--lg"
                autoResizeEnabled
                minHeight={112}
                placeholder="Full, clear description (no abbreviations)…"
                onValueChanged={(e) => setForm((f) => ({ ...f, currentIssue: e.value ?? '' }))}
              />
            </label>
            <label className="patient-form-grid__full">
              <span className="patient-form-label">Communication history (change log)</span>
              <TextArea
                value={form.changeLog ?? ''}
                className="patient-form-textarea--lg"
                autoResizeEnabled
                minHeight={112}
                onValueChanged={(e) => setForm((f) => ({ ...f, changeLog: e.value ?? '' }))}
              />
            </label>
            {!fieldPolicy?.hideInternalNote ? (
              <label className="patient-form-grid__full">
                <span className="patient-form-label">Internal note (pharmacy only)</span>
                <TextArea
                  value={form.internalNote ?? ''}
                  className="patient-form-textarea--lg"
                  autoResizeEnabled
                  minHeight={112}
                  onValueChanged={(e) => setForm((f) => ({ ...f, internalNote: e.value ?? '' }))}
                />
              </label>
            ) : null}
          </div>
        )}
      </RecordEditDiscussionPopup>

      <PatientFormPopup
        visible={patientPopupOpen}
        patient={null}
        initial={patientPopupInitial ?? undefined}
        pharmacies={pharmacies}
        coveredEntities={coveredEntities}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserAliases={currentUserAliases}
        onClose={() => setPatientPopupOpen(false)}
        onSaved={(p) => {
          onPatientSaved(p);
          setPatientPopupInitial(null);
        }}
      />
    </>
  );
});
